import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Languages as LanguagesIcon } from 'lucide-react';
import { EscortProfile } from '@/types';

interface LanguagesSectionProps {
  profile: EscortProfile;
  t: (key: string) => string; // Type for the translation function
}

// Flag mapping for common languages
const languageFlags: Record<string, string> = {
  'Français': '🇫🇷',
  'Anglais': '🇬🇧',
  'English': '🇬🇧',
  'Espagnol': '🇪🇸',
  'Spanish': '🇪🇸',
  'Italiano': '🇮🇹',
  'Italien': '🇮🇹',
  'Italian': '🇮🇹',
  'Deutsch': '🇩🇪',
  'Allemand': '🇩🇪',
  'German': '🇩🇪',
  'Russe': '🇷🇺',
  'Russian': '🇷🇺',
  'Japonais': '🇯🇵',
  'Japanese': '🇯🇵',
  'Chinois': '🇨🇳',
  'Chinese': '🇨🇳',
  'Arabe': '🇦🇪',
  'Arabic': '🇦🇪',
  'Portugais': '🇵🇹',
  'Portuguese': '🇵🇹'
};

// Language proficiency stars renderer
const renderLanguageStars = (level: number) => {
  return (
    <div className="flex">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg 
          key={i} 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill={i < level ? "currentColor" : "none"} 
          stroke={i < level ? "none" : "currentColor"}
          className={`w-4 h-4 ${i < level ? "text-amber-400" : "text-gray-300"}`}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
        </svg>
      ))}
    </div>
  );
};

export const LanguagesSection: React.FC<LanguagesSectionProps> = ({ profile, t }) => {
  // Handling multiple formats for languages data
  let languages: (string | { name: string; level: number })[] = [];

  // The languages property is nested within attributes
  if (profile.attributes?.languages && Array.isArray(profile.attributes.languages)) {
    languages = profile.attributes.languages;
  }
  // Remove the incorrect check for profile.languages directly
  // Format 2: Old format - in attributes (This check is now the primary one)
  // else if (profile.attributes?.languages && Array.isArray(profile.attributes.languages)) {
  //   languages = profile.attributes.languages;
  // }

  if (languages.length === 0) {
    return null; // Don't render section if no languages
  }

  return (
    <Card className="mb-6 border-pink-100">
      <CardContent className="p-0">
        <div className="bg-pink-50 py-3 px-6 border-b border-pink-100">
          <h3 className="text-lg font-medium text-pink-700 flex items-center">
            <LanguagesIcon size={18} className="mr-2 text-pink-500" />
            {t('profile.languages')}
          </h3>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {languages.map((language, index) => {
              // Handle different possible formats
              const languageName = typeof language === 'string' ? language : language.name;
              const languageLevel = typeof language === 'object' ? 
                (language.level || 5) : 5; // Default level if only string provided
              
              // Get appropriate flag or use a generic one
              const flag = languageFlags[languageName] || '🌐';

              return (
                <div key={index} className="flex justify-between items-center">
                  <span className="font-medium">
                    {flag} {languageName}
                  </span>
                  {renderLanguageStars(languageLevel)}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 